import { Inline, LinkButton, Tooltip, breakpoints } from '@etg/wings';
import { useDirection, useTranslation } from '@eti/providers';
import { Heart } from '@phosphor-icons/react';
import Media from 'react-media';
import { css } from '@eti/styles';
import type { TripType } from '../model';
import { useResultPageContext } from '../../contexts/ResultPageContextProvider';
import type { RebookingTripTypeType } from '../../../../../change-trip/models';

const saveTripTitle = css`
  text-decoration: none;

  p {
    color: #262626;
  }
`;

export interface SaveTripProps {
  type: TripType | RebookingTripTypeType;
  isSponsoredTrip?: boolean;
  isVI?: boolean;
  priceDisplayText: string;
  totalPrice: string;
}

export const SaveTrip = ({
  isSponsoredTrip,
  isVI,
  priceDisplayText,
  totalPrice,
  type,
}: SaveTripProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const {
    addSavedTrip,
    removeSavedTrip,
    savedTrips,
    selectedFlightDetails: { flight },
  } = useResultPageContext();
  const {
    bounds,
    selectionKey,
    shareableUrl,
    voucherAmount,
    paymentMethodPrices,
    availableExtraProducts,
    tripTravelers,
    travelerPrices,
    travelerPricesWithoutPaymentDiscounts,
  } = flight ?? {};
  const tripLiked = savedTrips.some((trip) => trip.selectionKey === selectionKey);

  const handleSavedTrip = () => {
    addSavedTrip({
      selectionKey,
      bounds,
      isSponsoredTrip,
      isVI,
      priceDisplayText,
      totalPrice,
      type,
      shareableUrl,
      voucherAmount,
      paymentMethodPrices,
      availableExtraProducts,
      tripTravelers,
      travelerPrices,
      travelerPricesWithoutPaymentDiscounts,
    });
  };

  const handleRemoveTrip = () => {
    removeSavedTrip(selectionKey);
  };

  const toolTipTitle = tripLiked
    ? t('Result.SaveTrip.Heart.Unsaved.Label')
    : t('Result.SaveTrip.Heart.Saved.Label');

  const heartIcon = (
    <Inline spacing={4}>
      <Heart
        color={tripLiked ? '#ff4957' : '#262626'}
        data-testid="resultPage-heartIcon"
        mirrored={isRTL}
        size={20}
        weight={tripLiked ? 'fill' : 'light'}
      />
    </Inline>
  );

  return (
    <LinkButton
      aria-label={
        tripLiked
          ? t('ScreenReader.Itinerary.Remove.Trip.AriaLabel.Text')
          : t('ScreenReader.Itinerary.Save.Trip.AriaLabel.Text')
      }
      className={saveTripTitle}
      onClick={!tripLiked ? handleSavedTrip : handleRemoveTrip}
    >
      <Inline spacing={4}>
        <Media query={`(min-width: ${breakpoints._768})`}>
          {(matches) =>
            matches ? (
              <Tooltip
                content={toolTipTitle}
                data-testid="data-testid"
                placement="top"
                title={toolTipTitle}
                trigger="hover"
              >
                {heartIcon}
              </Tooltip>
            ) : (
              heartIcon
            )
          }
        </Media>
        <p aria-hidden>
          {tripLiked ? t('Result.SaveTrip.Saved.Label') : t('Result.SaveTrip.Heart.Saved.Label')}
        </p>
      </Inline>
    </LinkButton>
  );
};
